/**
 * Client Dashboard — Phase E1 template
 *
 * Single React component that renders any of the 18 clients via dynamic cobrand.
 * Reads ?slug= or path /:slug and fetches:
 *   - /api/tenants/:slug → branding metadata
 *   - /api/work-items?tenant_id=:slug → board kanban
 *   - /api/cross-dept?tenant_id=:slug → cross-dept REQ panel
 *   - /api/artifacts?tenant_id=:slug → artifact list per dept
 *   - /api/approvals?tenant_id=:slug → approval queue
 *
 * Per dashboard-standards.md V2:
 *   - Fixed top tabs: Overview / Roadmap / Departments
 *   - Each dept has Board + Approvals + Risks + Verification Log
 *   - Account Manager naming (no "CEO" in UI)
 *
 * Phase E1 Sprint 1-4 will add:
 *   - Per-tenant cobranded color scheme via brand-config
 *   - Realtime via Durable Object SSE
 *   - i18n (TR primary)
 *
 * For now: minimal viable shell.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";

interface Tenant {
  id: string;
  slug: string;
  type: string;
  vertical: string;
  service_tier: string;
  status: string;
  mrr_usd: number;
  custom_domain: string | null;
}

interface WorkItem {
  id: string;
  title: string;
  status: string;
  priority: string;
  owner_department: string | null;
}

interface CrossDept {
  id: string;
  title: string;
  requester_department: string;
  assignee_department: string;
  status: string;
}

interface Approval {
  id: string;
  title: string;
  content_type: string;
  status: string;
  tier: number;
}

export default function ClientDashboard() {
  const { slug } = useParams<{ slug: string }>();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [crossDept, setCrossDept] = useState<CrossDept[]>([]);
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"overview" | "roadmap" | "departments">("overview");

  useEffect(() => {
    if (!slug) return;
    Promise.all([
      api<Tenant>(`/tenants/${slug}`),
      api<{ items: WorkItem[] }>(`/work-items?tenant_id=${slug}`),
      api<{ requests: CrossDept[] }>(`/cross-dept?tenant_id=${slug}`),
      api<{ approvals: Approval[] }>(`/approvals?tenant_id=${slug}`),
    ]).then(([t, wi, cd, ap]) => {
      setTenant(t);
      setWorkItems(wi.items);
      setCrossDept(cd.requests);
      setApprovals(ap.approvals);
      setLoading(false);
    });
  }, [slug]);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading {slug}…</div>;
  if (!tenant) return <div className="text-center py-12 text-red-600">Tenant not found: {slug}</div>;

  const pendingApprovals = approvals.filter((a) => a.status === "pending_approval");
  const activeBlockers = workItems.filter((w) => w.status === "blocked");

  return (
    <div className="space-y-6">
      {/* Header with cobrand */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{tenant.slug}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {tenant.vertical} · {tenant.service_tier} · {tenant.status} · MRR ${tenant.mrr_usd}/mo
          </p>
        </div>
        <span className={`text-xs px-2 py-1 rounded ${tenant.type === "internal_origin" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"}`}>
          {tenant.type}
        </span>
      </div>

      {/* Standard top tabs (per dashboard-standards.md) */}
      <div className="flex gap-1 border-b border-gray-200">
        {(["overview", "roadmap", "departments"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t ? "border-growtify-500 text-growtify-900" : "border-transparent text-gray-500"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Stat label="Work Items" value={workItems.length} />
            <Stat label="Cross-Dept Reqs" value={crossDept.length} />
            <Stat label="Onay Bekliyor" value={pendingApprovals.length} />
            <Stat label="Active Blockers" value={activeBlockers.length} />
          </div>

          {pendingApprovals.length > 0 && (
            <Section title="🔔 Onay Kuyruğu" subtitle="Account Manager onayı bekleyenler">
              <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
                {pendingApprovals.slice(0, 5).map((a) => (
                  <li key={a.id} className="px-4 py-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{a.title}</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-700">
                        Tier {a.tier} · {a.content_type}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {activeBlockers.length > 0 && (
            <Section title="⚠️ Active Blockers">
              <ul className="space-y-1">
                {activeBlockers.map((w) => (
                  <li key={w.id} className="text-sm text-red-600">• {w.title}</li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      )}

      {tab === "roadmap" && (
        <div className="text-center py-12 text-gray-400">
          Roadmap view — Phase E1 Sprint 2 will add quarterly timeline + pillar progress.
        </div>
      )}

      {tab === "departments" && (
        <div className="space-y-4">
          {departmentsFromWorkItems(workItems).map((dept) => (
            <DeptSection key={dept} dept={dept} workItems={workItems.filter((w) => w.owner_department === dept)} />
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-sm font-medium uppercase tracking-wide text-gray-500 mb-1">{title}</h2>
      {subtitle && <p className="text-xs text-gray-400 mb-2">{subtitle}</p>}
      {children}
    </div>
  );
}

function departmentsFromWorkItems(items: WorkItem[]): string[] {
  const set = new Set(items.map((w) => w.owner_department).filter(Boolean) as string[]);
  return Array.from(set).sort();
}

function DeptSection({ dept, workItems }: { dept: string; workItems: WorkItem[] }) {
  // Standard sub-tabs per dashboard-standards.md V2: Board / Approvals / Risks / Verification Log
  const cols: Record<string, WorkItem[]> = {
    "Planned": workItems.filter((w) => w.status === "planned"),
    "In Progress": workItems.filter((w) => w.status === "in_progress"),
    "Reported": workItems.filter((w) => w.status === "reported_done"),
    "Verified": workItems.filter((w) => w.status === "verified" || w.status === "completed" || w.status === "closed"),
    "Blocked": workItems.filter((w) => w.status === "blocked"),
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="font-medium text-gray-900 mb-3">{dept}</h3>
      <div className="grid grid-cols-5 gap-2 text-xs">
        {Object.entries(cols).map(([col, list]) => (
          <div key={col}>
            <div className="font-medium text-gray-600 mb-2">{col} ({list.length})</div>
            <div className="space-y-1">
              {list.map((w) => (
                <div key={w.id} className="rounded bg-gray-50 p-1.5 text-gray-700">{w.title}</div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
